import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { GlobalAppSDK } from '@host/controllers/GlobalAppController';

// Utility functions for panel size persistence
const PANEL_SIZE_STORAGE_KEY = 'nexgen-panel-sizes';

type PanelSizePreference = {
  width: number;
  height: number | string;
};

const getPanelSizeKey = (appId: string, viewName?: string) => {
  return viewName ? `${appId}-${viewName}` : appId;
};

const loadPanelSizes = (): Record<string, PanelSizePreference> => {
  try {
    const stored = localStorage.getItem(PANEL_SIZE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to load panel sizes from localStorage:', error);
    return {};
  }
};

const savePanelSize = (appId: string, viewName: string | undefined, size: PanelSizePreference) => {
  try {
    const sizes = loadPanelSizes();
    const key = getPanelSizeKey(appId, viewName);
    sizes[key] = size;
    localStorage.setItem(PANEL_SIZE_STORAGE_KEY, JSON.stringify(sizes));
  } catch (error) {
    console.warn('Failed to save panel size to localStorage:', error);
  }
};

const getSavedPanelSize = (appId: string, viewName?: string): PanelSizePreference | null => {
  const sizes = loadPanelSizes();
  const key = getPanelSizeKey(appId, viewName);
  return sizes[key] || null;
};

// Debounce helper
let saveTimeouts: Record<string, number> = {};
const debouncedSavePanelSize = (
  appId: string,
  viewName: string | undefined,
  size: PanelSizePreference,
  delay = 500
) => {
  const key = getPanelSizeKey(appId, viewName);
  if (saveTimeouts[key]) {
    clearTimeout(saveTimeouts[key]);
  }
  saveTimeouts[key] = setTimeout(() => {
    savePanelSize(appId, viewName, size);
    delete saveTimeouts[key];
  }, delay);
};

type PanelId = string;

export type PanelState = {
  id: PanelId;
  appId: string;
  title: string;
  x: number;
  y: number;
  width: number;
  height: number | string;
  zIndex: number;
  isMinimized: boolean;
  isHidden?: boolean;
  isMaximized: boolean;
  manifest?: any; // app manifest
  viewName?: string; // specific view to render
  // constraints (optional)
  minWidth?: number;
  minHeight?: number;
  canClose?: boolean;
  canMaximize?: boolean;
  content?: React.ReactNode;
};

export interface openProps {
  title: string;
  appId: string;
  viewName: string;
}

export type PanelStore = {
  panels: PanelState[];
  activeId: PanelId | null;
  nextZ: number;
  open: (props: openProps) => void;
  close: (id: PanelId) => void;
  closeByAppId: (appId: string) => void;
  focus: (id: PanelId) => void;
  setRect: (id: PanelId, rect: Partial<Pick<PanelState, 'x' | 'y' | 'width' | 'height'>>) => void;
  setFlags: (id: PanelId, flags: Partial<Pick<PanelState, 'isMinimized' | 'isMaximized'>>) => void;
  updateContent: (id: PanelId, content: React.ReactNode) => void;
  closeAll: () => void;
};

export const usePanelStore = create<PanelStore>()(
  subscribeWithSelector((set, get) => ({
    panels: [],
    activeId: null,
    nextZ: 100,

    open: (props: openProps) => {
      const { title, appId, viewName } = props;
      const currentState = get();
      const existingPanel = currentState.panels.find(
        (p) => p.appId === appId && p.viewName === viewName
      );
      if (existingPanel) {
        const zIndex = currentState.nextZ;
        set((state) => ({
          panels: state.panels.map((p) =>
            p.id === existingPanel.id ? { ...p, isHidden: false, zIndex } : p
          ),
          activeId: existingPanel.id,
          nextZ: zIndex + 1,
        }));

        return existingPanel.id;
      }

      const apps = GlobalAppSDK.getAllApps();
      const manifestData = apps.filter((value) => value.id === appId)[0]?.controller.manifest;

      const id = crypto.randomUUID();

      const zIndex = currentState.nextZ;

      const newPanel: PanelState = {
        id,
        appId,
        title,
        manifest: manifestData,
        viewName,
        x: 100 + currentState.panels.length * 30, // Offset new panels
        y: 100 + currentState.panels.length * 30,
        width: 1000,
        height: '85%',
        zIndex,
        isMinimized: false,
        isMaximized: false,
        minWidth: 260,
        minHeight: 160,
        canClose: true,
        canMaximize: true,
      };

      // Load saved panel size from localStorage if available - only for chat
      if (appId === 'chat') {
        const savedSize = getSavedPanelSize(appId, viewName);
        if (savedSize) {
          // Validate and clamp saved dimensions to prevent corrupted data
          const savedWidth =
            typeof savedSize.width === 'number' && !isNaN(savedSize.width)
              ? Math.max(savedSize.width, newPanel.minWidth || 260)
              : newPanel.width;
          const savedHeight =
            typeof savedSize.height === 'number' && !isNaN(savedSize.height)
              ? Math.max(savedSize.height, newPanel.minHeight || 160)
              : newPanel.height;

          newPanel.width = savedWidth;
          newPanel.height = savedHeight;
        }
      }

      if (appId === 'breakoutSession') {
        newPanel.width = 320;
        newPanel.height = '85%';
        newPanel.minHeight = 450;
        newPanel.canClose = false;
        newPanel.canMaximize = false;
        //is on the right of the screen
        newPanel.x = window.innerWidth - newPanel.width - 300;
        newPanel.y = 100;
      }

      set((state) => ({
        panels: [...state.panels, newPanel],
        activeId: id,
        nextZ: zIndex + 1,
      }));

      return id;
    },
    closeByAppId: (appId) => {
      set((state) => ({
        panels: state.panels.map((p) => (p.appId === appId ? { ...p, isHidden: true } : p)),
        activeId:
          state.activeId && state.panels.find((p) => p.appId === appId && p.id === state.activeId)
            ? null
            : state.activeId,
      }));
    },

    close: (id) => {
      set((state) => ({
        panels: state.panels.map((p) => (p.id === id ? { ...p, isHidden: true } : p)),
        activeId: state.activeId === id ? null : state.activeId,
      }));
    },

    closeAll: () => {
      set((state) => ({
        panels: state.panels.map((p) => ({ ...p, isHidden: true })),
        activeId: null,
      }));
    },

    focus: (id) => {
      const currentState = get();
      const panel = currentState.panels.find((p) => p.id === id);
      if (panel && currentState.activeId !== id) {
        const zIndex = currentState.nextZ;
        set((state) => ({
          panels: state.panels.map((p) => (p.id === id ? { ...p, zIndex } : p)),
          activeId: id,
          nextZ: zIndex + 1,
        }));
      }
    },

    setRect: (id, rect) => {
      set((state) => {
        const panel = state.panels.find((p) => p.id === id);
        if (!panel) return state;

        // Calculate updated dimensions, handling string heights (e.g., '85%')
        const updatedWidth =
          rect.width !== undefined
            ? Math.max(rect.width, panel.minWidth || 260)
            : typeof panel.width === 'number'
              ? panel.width
              : 1000;

        const updatedHeight =
          rect.height !== undefined ? Math.max(rect.height, panel.minHeight || 160) : panel.height; // Keep existing height (may be string like '85%')

        // Save to localStorage only on actual resize events (not drag-only) - only for chat
        if (panel.appId === 'chat' && (rect.width !== undefined || rect.height !== undefined)) {
          debouncedSavePanelSize(panel.appId, panel.viewName, {
            width: updatedWidth,
            height: typeof updatedHeight === 'number' ? updatedHeight : panel.height,
          });
        }

        return {
          panels: state.panels.map((p) =>
            p.id === id
              ? {
                  ...p,
                  ...rect,
                  width: updatedWidth,
                  height: updatedHeight,
                }
              : p
          ),
        };
      });
    },

    setFlags: (id, flags) => {
      set((state) => ({
        panels: state.panels.map((p) => (p.id === id ? { ...p, ...flags } : p)),
      }));
    },

    updateContent: (id, content) => {
      set((state) => ({
        panels: state.panels.map((p) => (p.id === id ? { ...p, content } : p)),
      }));
    },
  }))
);
